import { useEffect } from 'react';

import {
  Grid,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from '@mui/material';

import TraceModeSelector from './TraceModeSelector';

import { WindowForm } from './types';
import { Controller, useForm } from 'react-hook-form';
import AnalyzerSelectLocal from 'features/analyzers/AnalyzerSelectLocal';
import SnapshotSelectLocal from 'features/snapshots/SnapshotSelectLocal';

import { useAnalyzers } from 'features/analyzers/api/getAnalyzers';
import { useSnapshots } from 'features/snapshots/api/getSnapshots';

interface Props {
  handleClose: () => void;
  window: WindowForm | null;
  updateWindow: (window: WindowForm) => void;
}

const defaultValues: WindowForm = {
  id: -1,
  analyzer_id: -1,
  snapshot_id: -1,
  wall_id: -1,
  pos_x: -1,
  pos_y: -1,
  trace_mode: 0,
};

const WindowAnalyzerSelector = ({
  handleClose,
  window,
  updateWindow,
}: Props) => {
  const { data: analyzers } = useAnalyzers({});

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<WindowForm>({
    defaultValues,
  });

  const { data: snapshots } = useSnapshots({
    analyzerId: watch('analyzer_id'),
  });

  const onSubmit = () => {
    const formState = getValues();
    updateWindow(formState);
    handleClose();
  };

  const isDisabled = () => {
    //Decides when option is greyed out or not
    return (
      !isDirty || (watch('analyzer_id') !== -1 && watch('snapshot_id') === -1)
    );
  };

  useEffect(() => {
    // Anytime analyzer_id changes, this effect will run and reset snapshot_id to -1
    setValue('snapshot_id', -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('analyzer_id')]); // <--- Dependency array to trigger effect when analyzer_id changes

  useEffect(() => {
    if (window) {
      setValue('id', window.id);
      setValue('analyzer_id', window.analyzer_id);
      setValue('snapshot_id', window.snapshot_id);
      setValue('wall_id', window.wall_id);
      setValue('pos_x', window.pos_x);
      setValue('pos_y', window.pos_y);
      setValue('trace_mode', window.trace_mode);
    } else reset(defaultValues);
  }, [window, setValue, reset]);

  return (
    <Dialog open={!!window} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>Edit Window</DialogTitle>
      <DialogContent>
        <Grid container justifyContent='center' spacing={2}>
          <Grid item xs={12} />
          <Controller
            name='analyzer_id'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={12}>
                <AnalyzerSelectLocal
                  analyzers={analyzers}
                  selectedAnalyzerId={value}
                  fullWidth
                  handleAnalyzerChanged={onChange}
                />
              </Grid>
            )}
          />
          <Controller
            name='snapshot_id'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={12}>
                <SnapshotSelectLocal
                  snapshots={snapshots}
                  selectedSnapshotId={value}
                  handleSnapshotChanged={onChange}
                  fullWidth
                />
              </Grid>
            )}
          />
          <Controller
            name='trace_mode'
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value } }) => (
              <Grid item xs={12}>
                <TraceModeSelector
                  traceMode={value}
                  handleTraceModeChanged={onChange}
                />
              </Grid>
            )}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant='contained' color='secondary'>
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit(onSubmit)()}
          disabled={isDisabled()}
          variant='contained'
          color='primary'
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WindowAnalyzerSelector;
